import { useRouter } from 'next/router';
import pluralize from 'pluralize';

import { Button } from '@/components/button';
import { CarrierLogo } from '@/components/carrier-logo';
import { Text } from '@/components/text';
import { useTruncated } from '@/hooks/use-truncated';
import { MODE_WATER } from '@/lib/constants';
import { RouteSummaryOptionModel } from '@/lib/models/route-summary-option/types';
import { formatFrequency } from '@/utils/format';
import { createSearchPageUrlUsingKeys } from '@/utils/helpers/page-urls';
import { tv } from '@/utils/styles';
import { trackUser } from '@/utils/tracking';

const MAX_ROWS = 3;

interface Props {
  readonly options: RouteSummaryOptionModel[];
}

const PageRouteLines = ({ options }: Props) => {
  const router = useRouter();
  const { origin, destination } = router.query;
  const { rows, remainingRows, shouldShowLoadMore, shouldShowAllRows, showAllRows } = useTruncated(options, MAX_ROWS);
  const mode = MODE_WATER;

  const handleShowAll = () => {
    showAllRows();

    if (!shouldShowLoadMore) {
      // Analytics Event
      trackUser.event('RoutePage More Carriers', {
        mode,
      });
    }
  };

  const handleMoreDetailsClick = () => {
    // Analytics Event
    trackUser.event('RoutePage Card CTA', {
      mode,
      origin: origin?.toString(),
      destination: destination?.toString(),
    });
  };

  const { base, table, head, headTr, tr, td } = styles();

  return (
    <div className={base()}>
      <table className={table()}>
        <thead>
          <tr className={head()}>
            <th className={headTr()}>Service Lines</th>
            <th className={headTr()}>Service Type</th>
            <th className={headTr()}>Departure frequency</th>
            <th className={headTr()}>Servicing Carriers</th>
          </tr>
        </thead>
        <tbody>
          {options.length > 0 &&
            options.map((row, index) => {
              const { carrierNames, carrierIdentifiers } = row;
              const shouldHideRow = index >= rows.length;

              return (
                <tr className={tr({ className: shouldHideRow && 'hidden' })} key={row.id}>
                  <td className={td({ className: 'font-medium' })}>
                    <div className="flex items-center">
                      {carrierIdentifiers.length ? (
                        <CarrierLogo carrierCode={carrierIdentifiers[0].value} mode={mode} className="mr-2" />
                      ) : null}
                      {row.lineNames.join(' → ')}
                    </div>
                  </td>
                  <td className={td()}>{row.transfers > 0 ? 'Transshipment' : 'Direct'}</td>
                  <td className={td()}>{formatFrequency(row.frequency)}</td>
                  <td className={td({ className: 'text-baseSm' })}>{carrierNames.join(', ')}</td>
                </tr>
              );
            })}
          {shouldShowLoadMore && !shouldShowAllRows && (
            <tr className={tr({ className: 'border-none' })} onClick={handleShowAll}>
              <td className={td()} colSpan={3}>
                <Button size="small" variant="secondaryLight">
                  {shouldShowAllRows ? `Show less` : `+ ${remainingRows} more ${pluralize('service', remainingRows)}`}
                </Button>
              </td>
            </tr>
          )}
          <tr className={tr()}>
            <td className={td({ className: 'pt-4' })}>
              <Button
                href={createSearchPageUrlUsingKeys(origin, destination)}
                onClick={handleMoreDetailsClick}
                target="_blank"
                size="small"
                iconRight="arrow-right-alt"
              >
                More Details
              </Button>
            </td>
            <td className={td({ className: 'pt-4' })} colSpan={2}>
              <Text variant="sm" className="m-0 italic text-text-secondary">
                See carrier information, sailing schedules and estimated emissions
              </Text>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

const styles = tv({
  slots: {
    base: 'w-full overflow-x-auto',
    table: 'w-full border-collapse',
    head: 'border-b border-grey-200 text-sm text-grey-500',
    headTr: 'whitespace-nowrap pb-2 pr-4 text-left font-normal last:hidden md:last:table-cell',
    tr: 'border-t border-grey-200 text-baseSm',
    td: 'py-2 pr-4 align-middle leading-snug last:hidden md:last:table-cell',
  },
});

export { PageRouteLines };
